import {useState, useEffect, useMemo} from 'react';

export const statuses = {
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
};

const usePromise = (promise) => {
  const [promiseState, setPromiseState] = useState({
    status: statuses.PENDING,
    data: undefined,
    error: undefined
  });
  useEffect(() => {
    let effectCanceled = false;
    const maybeResolve = (data) => {
      if (effectCanceled) return;
      setPromiseState({
        status: statuses.RESOLVED,
        data
      });
    };
    const maybeReject = (error) => {
      if (effectCanceled) return;
      setPromiseState({
        status: statuses.REJECTED,
        error
      });
    };
    promise.then(maybeResolve, maybeReject);
    return () => {
      effectCanceled = true;
    };
  }, [promise]);
  return {
    ...promiseState,
    isPending: promiseState.status === statuses.PENDING
  };
};

/**
 * @param  {function} promiseFn - a function that returns a promise
 * @param  {array} dependencies - an array of dependencies
 *
 * usePromiseMemo will only recompute the memoized promise when one of the dependencies has changed. usePromiseMemo should be used when in need of resolving a promise that might otherwise be often recomputed. See https://reactjs.org/docs/hooks-reference.html#usememo for more.
 */
export const usePromiseMemo = (promiseFn, dependencies) => {
  const promise = useMemo(async() => {
    return await promiseFn();
  }, [...dependencies]);
  return usePromise(promise);
};

export default usePromise;
